import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import { Container, Card, Button, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import axios from '../../services/axios';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';
import DataTable from '../../components/DataTable';
import ButtonModal from '../../components/ModalConfirmation/ButtonModal';
import { useNotification } from '../../components/Notification/NotificationContext';
import ErrorProcessor from '../../components/ErrorProcessor';
import CardHeaderMore from '../../components/Cards/CardHeaderMore';

export default function WhatsAppProviders() {
  const routeName = '/whatsappproviders';
  const dataName = 'Provedor WhatsApp';
  const titleName = 'Provedores de WhatsApp';
  const navigate = useNavigate();
  const [apiErrors, setApiErrors] = useState({});
  const notification = useNotification();
  const [data, setData] = useState([]);
  const [qrcode, setQrcode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [qrcodeModal, setQrcodeModal] = useState(false);
  const [qrcodeProviderCheck, setQrcodeProviderCheck] = useState(null);
  const userData = useSelector((state) => state.auth.user);
  const tableColumns = useMemo(
    () => [
      {
        Header: 'Nome',
        accessor: 'name',
      },
      {
        Header: 'Empresa',
        accessor: 'company_name',
      },
      {
        Header: 'Status de conexão',
        accessor: 'status_name',
      },
      {
        Header: 'Tipo de provedor',
        accessor: 'whatsapp_provider_type_name',
      },
    ],
    []
  );

  const getData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(routeName);
      setData(response.data);
    } catch (err) {
      setApiErrors(err);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const confirmDelete = useCallback(async (id) => {
    try {
      setIsLoading(true);
      await axios.delete(`${routeName}/${id}`);
      notification(`${dataName} removido com sucesso`, 'success');
      getData();
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const confirmDisconnect = useCallback(async (id) => {
    try {
      setIsLoading(true);
      await axios.delete(`${routeName}/disconnect/${id}`);
      setTimeout(async () => {
        await getData();
        notification(`${dataName} desconectado com sucesso`, 'success');
        setIsLoading(false);
      }, 1000);
    } catch (err) {
      setApiErrors(err);
      setIsLoading(false);
    }
  }, []);

  const clearQrcode = useCallback(() => {
    setQrcodeModal(false);
    setQrcode(null);
    setQrcodeProviderCheck(null);
  }, [setQrcodeModal, setQrcode, setQrcodeProviderCheck]);

  const qrcodeCheckStatus = useCallback(async () => {
    try {
      if (!qrcodeProviderCheck) {
        return;
      }

      const getProvider = await axios.get(
        `${routeName}/${qrcodeProviderCheck}`
      );
      if (getProvider.data.status.state === 'open') {
        clearQrcode();
        notification(`QRCode autenticado com sucesso`, 'success');
        getData();
      }
    } catch (err) {
      setApiErrors(err);
      console.error(err);
    }
  }, [qrcodeProviderCheck]);

  const qrcodeRequest = useCallback(
    async (id) => {
      try {
        setIsLoading(true);
        const getStatus = await axios.get(`${routeName}/connect/${id}`);
        setQrcode(getStatus.data.status.base64);
        setQrcodeModal(true);
        setQrcodeProviderCheck(id);
        setIsLoading(false);
      } catch (err) {
        setApiErrors(err);
        setIsLoading(false);
      }
    },
    [setQrcode, setQrcodeModal, setQrcodeProviderCheck]
  );

  const tableData = useMemo(() => {
    return data.map((provider) => {
      const linksAction = [
        {
          onclick: () => {
            navigate(`${routeName}/${provider.id}/edit`);
          },
          faicon: 'faEdit',
          text: 'Atualizar',
        },
      ];

      switch (provider.WhatsAppProviderType.type) {
        case 'evolution_api_waw':
          if (provider.status && provider.status.state !== 'open') {
            linksAction.push({
              custom: (
                <ButtonModal
                  message=""
                  buttonProps={{ text: 'Abrir QRCode', faicon: 'faQrcode' }}
                  modalProps={{ noFooter: true }}
                  beforeOpen={() => qrcodeRequest(provider.id)}
                  textModal={{
                    title: 'QRCode WhatsApp Web',
                  }}
                  dropdown
                />
              ),
            });
          } else if (provider.status && provider.status.state === 'open') {
            linksAction.push({
              custom: (
                <ButtonModal
                  onConfirm={() => confirmDisconnect(provider.id)}
                  message={`Confirma desconexão do WhatsApp Web da conta ${provider.name} ?`}
                  buttonProps={{ text: 'Desconectar', faicon: 'faPowerOff' }}
                  textModal={{
                    title: `Desconectar ${dataName}`,
                    textConfirm: 'Desconectar',
                  }}
                  dropdown
                />
              ),
            });
          }
          break;
        default:
          break;
      }

      if (userData.isadmin === 1) {
        linksAction.push({
          custom: (
            <ButtonModal
              onConfirm={() => confirmDelete(provider.id)}
              message={`Confirma remoção da ${dataName} ${provider.name}?`}
              buttonProps={{ text: 'Remover', faicon: 'faTrash' }}
              textModal={{
                title: `Remover ${dataName}`,
                textConfirm: 'Remover',
              }}
              dropdown
            />
          ),
        });
      }

      let statusName = (
        <span className="text-secondary fw-bold">Indisponível</span>
      );
      if (provider.status) {
        switch (provider.status.state) {
          case 'open':
            statusName = (
              <span className="text-success fw-bold">Conectado</span>
            );
            break;
          default:
            statusName = (
              <span className="text-danger fw-bold">Desconectado</span>
            );
            break;
        }
      }

      return {
        company_name: provider.Company.name,
        whatsapp_provider_type_name: (
          <div className="d-flex justify-content-between">
            <span>{provider.WhatsAppProviderType.name}</span>
            <CardHeaderMore links={linksAction} className="" />
          </div>
        ),
        status_name: statusName,
        ...provider,
      };
    });
  }, [data, userData]);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    let intervalId;

    if (qrcodeProviderCheck) {
      intervalId = setInterval(() => {
        qrcodeCheckStatus();
      }, 2000);
    } else {
      clearInterval(intervalId);
    }

    return () => clearInterval(intervalId);
  }, [qrcodeProviderCheck, qrcodeCheckStatus]);

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Modal show={qrcodeModal} onHide={() => clearQrcode()} centered>
        <Modal.Header closeButton className="border-0 bg-gray-100">
          <Modal.Title as="h5" className="text-uppercase">
            QRCode WhatsApp Web
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap justify-content-center">
            <img src={qrcode} alt="QRCODE" />
          </div>
        </Modal.Body>
      </Modal>
      <Breadcrumbs title="Provedores de Whatsapp" />
      <div className="page-header d-flex justify-content-between align-items-center">
        <h1 className="page-heading">{titleName}</h1>
        <div>
          <Link to={`${routeName}/new`}>
            <Button variant="primary" className="text-uppercase">
              <FaPlus /> Cadastrar
            </Button>
          </Link>
        </div>
      </div>
      <section>
        <Card className="card-table mb-5">
          <ErrorProcessor error={apiErrors} />
          <DataTable
            items={tableData}
            columns={tableColumns}
            defaultPageSize={5}
          />
        </Card>
      </section>
    </Container>
  );
}
